import { z } from "zod/v4";
import {
  apiDefinition,
  type GeminiVoice,
  knownGeminiVoices,
} from "./client/src/apiTyping.ts";

export const preferredNetworkValues = [
  "alice-and-bot",
  "email",
  "facebook-messenger",
  "instagram",
  "phone-call",
  "slack",
  "sms",
  "teams",
  "telegram",
  "telegram-demo",
  "whatsapp",
  "whatsapp-demo",
  "whatsapp-demo-business-integration",
  "whatsapp-for-business",
] as const;

export type PreferredNetwork = (typeof preferredNetworkValues)[number];
export { type GeminiVoice, knownGeminiVoices };

const createActionSharedParams = z.object({
  targetConversationId: z.string().optional().describe(
    "Optional explicit target conversation ID override (e.g. a WhatsApp group id ending with @g.us, a Slack user or conversation ID, or a Teams user ID). Provide this when the task should be executed in a specific conversation.",
  ),
  contactName: z.string().optional().describe(
    "Optional friendly display name of the contact/user (e.g. 'Sarah', 'Uri').",
  ),
  contactPhone: z.string().optional().describe(
    "Phone number to call or send SMS to (e.g. +1234567890 or 972501234567).",
  ),
  description: z.string().describe(
    "Description of what needs to be done, including context of why and who this is for. Formulate as an internal thought.",
  ),
  preferredNetwork: z.enum(preferredNetworkValues).describe(
    `Which messaging network to use for the scheduled action, valid options are: ${
      preferredNetworkValues.join(", ")
    }`,
  ),
  recurrenceRule: z.string().optional().describe(
    "Recurrence rule for the scheduled action. Accepts: 'daily', 'weekly', 'monthly', a number (ms), or a full RFC 5545 RRULE string (e.g., 'RRULE:FREQ=DAILY'). Prefer the RRULE format for advanced recurrence. Leave empty for non-recurring tasks.",
  ),
  recurrenceEnd: z.string().optional().describe(
    "End date/time for recurrence (optional, in local time format).",
  ),
  subject: z.string().optional().describe(
    "Email subject line. Required when preferredNetwork is 'email', must not be provided otherwise.",
  ),
  whatsappTemplateName: z.string().optional().describe(
    "Approved WhatsApp Business template name to send. Only valid when preferredNetwork is 'whatsapp' on a bot connected via the Official WhatsApp Business API. Template must already be APPROVED in Meta.",
  ),
  whatsappTemplateLanguage: z.string().optional().describe(
    "Language code (e.g. en_US) of the approved WhatsApp Business template. Required when whatsappTemplateName is set.",
  ),
  whatsappTemplateVariables: z.array(z.string()).optional().describe(
    "Positional variable values for the WhatsApp Business template body, filling {{1}}, {{2}}, ... in order. Meta API strictly prohibits newlines (\\n), tabs (\\t), or 4+ consecutive spaces in parameter text. If omitted, the action description fills {{1}}.",
  ),
  whatsappTemplateHeaderImageUrl: z.string().optional().describe(
    "Optional header image URL when sending a WhatsApp Business template that has an IMAGE header component.",
  ),
  phoneCallVoice: z.enum(knownGeminiVoices).optional().describe(
    "Voice to use when preferredNetwork is 'phone-call'. Male options: 'Charon' (calm/baritone), 'Puck' (upbeat/tenor), 'Fenrir'. Female options: 'Aoede' (breezy/soprano), 'Kore' (firm/soprano). Select a voice fitting your gender and persona.",
  ),
  phoneCallLanguage: z.string().optional().describe(
    "Spoken language for the phone call (e.g. 'he', 'en', 'ar', 'es'). Match the recipient's and context's language.",
  ),
  phoneCallGreeting: z.string().optional().describe(
    "Opening spoken greeting to say immediately when the user answers. Must be 1 short, natural sentence in the call language.",
  ),
});

export const createActionParamsSchema = createActionSharedParams.extend({
  runAt: z.string().optional().describe(
    "Leave empty for ASAP. Local time format expected.",
  ),
  targetConversationId: z.string().nullable().describe(
    "The conversation ID where this task should be executed. Use null to target the current conversation. For WhatsApp groups use the group ID ending with @g.us, for 1:1 WhatsApp use the ID ending with @c.us (e.g. 972501234567@c.us), for Slack use a user or conversation ID, and for Teams use the Teams user ID.",
  ),
  aliceChatUserId: z.string().nullable().optional().describe(
    "For alice-and-bot network, you can explicitly set the user ID (publicSignKey) here. Omit for anonymous users if you just have the conversation ID.",
  ),
});

export const createRemoteActionInputSchema =
  apiDefinition["create-remote-task"].input;

type ScheduledActionParams = z.infer<typeof createActionParamsSchema>;

export type CreateActionTimestampInput =
  & Omit<ScheduledActionParams, "runAt" | "preferredNetwork">
  & { runAt?: number; preferredNetwork?: string };

export type CreateScheduledActionInput =
  & Omit<ScheduledActionParams, "runAt">
  & { runAt?: string | number };

export type CreateRemoteActionInput = z.infer<
  typeof createRemoteActionInputSchema
>;

export const injectContextInputSchema = apiDefinition["inject-context"].input;

export type InjectContextInput = z.infer<typeof injectContextInputSchema>;

export const injectReplyInputSchema = apiDefinition["inject-reply"].input;

export type InjectReplyInput = z.infer<typeof injectReplyInputSchema>;

export const pauseInteractionInputSchema =
  apiDefinition["pause-interaction"].input;

export type PauseInteractionInput = z.infer<typeof pauseInteractionInputSchema>;

export const listScheduledTasksInputSchema =
  apiDefinition["list-scheduled-tasks"].input;

export type ListScheduledTasksInput = z.infer<
  typeof listScheduledTasksInputSchema
>;

export const deleteScheduledTaskInputSchema =
  apiDefinition["delete-scheduled-task"].input;

export type DeleteScheduledTaskInput = z.infer<
  typeof deleteScheduledTaskInputSchema
>;
